'use client';

import { Chip } from '@heroui/chip';
import { Avatar } from '@heroui/avatar';
import Link from 'next/link';
import type { Route } from 'next';
import type { QuoteRequest } from '@/util/types/serverTypeCollection/repairList';
import QuoteEntryActionsDropdown from './QuoteEntryActionsDropdown';
import {
  buildQuoteActionItems,
  formatQuoteCurrency,
  formatQuoteEntryDate,
  getQuoteRequestStatusColor,
  handleQuoteEntryAction,
  type QuoteActionKey,
} from './quoteEntryUtils';

type RepairCompanySummary = {
  _id: string;
  name: string;
  logo?: string;
};

export default function QuoteRequestEntryRow({
  quote,
  repairCompany,
  repairListId,
  reportId,
  companyColor,
  canViewQuoteDetails,
  buildReportUrl,
  buildRepairListViewUrl,
  menuPortalContainer,
}: {
  quote: QuoteRequest;
  repairCompany?: RepairCompanySummary;
  repairListId: string;
  reportId?: string;
  companyColor?: string;
  canViewQuoteDetails: boolean;
  buildReportUrl?: (reportId: string) => string;
  buildRepairListViewUrl: (repairListId: string, itemIds: string[]) => string;
  menuPortalContainer?: Element;
}) {
  const itemsSentCount = quote.repairItemIds?.length || 0;

  const onAction = (key: QuoteActionKey) =>
    handleQuoteEntryAction({
      key,
      itemIds: quote.repairItemIds || [],
      pdfUrl: quote.pdfUrl,
      quoteResponseUrl: quote.quoteResponseUrl,
      buildRepairListViewUrl,
      repairListId,
    });

  return (
    <div className='p-3'>
      <div className='flex flex-wrap items-center justify-between'>
        <div className='flex-1'>
          <div className='mb-2 flex items-center gap-2'>
            <div className='flex items-center gap-2'>
              {repairCompany?.logo && (
                <Avatar
                  src={repairCompany.logo}
                  alt={repairCompany.name}
                  size='sm'
                  className='h-5 w-5'
                />
              )}
              {reportId && buildReportUrl ? (
                <Link
                  href={buildReportUrl(reportId) as Route}
                  className='text-sm font-medium text-gray-900 transition-colors hover:text-(--company-color)'
                  style={{
                    ['--company-color' as any]: companyColor || '#000000',
                  }}
                  scroll={false}
                >
                  {repairCompany?.name || 'Unknown Company'}
                </Link>
              ) : (
                <span className='text-sm font-medium text-gray-900'>
                  {repairCompany?.name || 'Unknown Company'}
                </span>
              )}
            </div>
            <Chip
              size='sm'
              variant='flat'
              color={getQuoteRequestStatusColor(quote.status) as any}
              className='text-xs'
            >
              {quote.status}
            </Chip>
          </div>
          <div className='mb-1 text-xs text-gray-600'>
            <span>
              {itemsSentCount} item{itemsSentCount !== 1 ? 's' : ''} submitted
            </span>
            <span className='mx-2'>•</span>
            <span>
              {quote.status === 'received' && quote.quotedAt
                ? `Quoted on ${formatQuoteEntryDate(quote.quotedAt)}`
                : `Requested on ${formatQuoteEntryDate(quote.requestedAt)}`}
            </span>
          </div>
          {quote.quotedAmount && canViewQuoteDetails && (
            <div className='flex items-center gap-1 text-sm font-semibold text-green-600'>
              Quoted Amount:
              <span>{formatQuoteCurrency(quote.quotedAmount)}</span>
            </div>
          )}
        </div>
        <div className='ml-3 shrink-0'>
          <QuoteEntryActionsDropdown
            items={buildQuoteActionItems({
              pdfUrl: quote.pdfUrl,
              quoteResponseUrl: quote.quoteResponseUrl,
              showQuoteResponse: quote.status === 'received' && canViewQuoteDetails,
            })}
            onAction={onAction}
            portalContainer={menuPortalContainer}
          />
        </div>
      </div>
    </div>
  );
}
